import { useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const touchStartX = useRef(null);
  const open = index !== null && index >= 0 && images.length > 0;
  const current = open ? images[index] : null;
  const many = images.length > 1;

  const prev = useCallback(() => {
    onNavigate((index - 1 + images.length) % images.length);
  }, [index, images.length, onNavigate]);

  const next = useCallback(() => {
    onNavigate((index + 1) % images.length);
  }, [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && many) prev();
      if (e.key === "ArrowRight" && many) next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, many, onClose, prev, next]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 50 && many) {
      if (dx > 0) prev(); else next();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-stone-950/95 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            data-testid="lightbox-close-btn"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/25 transition"
            aria-label="Tutup"
          >
            <X className="w-5 h-5" />
          </button>

          {many && (
            <button
              type="button"
              data-testid="lightbox-prev-btn"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 md:left-6 z-10 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/25 transition"
              aria-label="Foto sebelumnya"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="max-w-5xl w-full px-12 md:px-20"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.url}
              alt={current.caption || "Foto ukuran penuh"}
              data-testid="lightbox-image"
              className="w-full max-h-[78vh] object-contain rounded-2xl select-none"
              draggable={false}
            />
            <figcaption className="mt-3 flex items-center justify-center gap-3 text-center">
              {current.caption && (
                <span data-testid="lightbox-caption" className="font-display font-bold text-white text-sm md:text-base">{current.caption}</span>
              )}
              {many && (
                <span data-testid="lightbox-counter" className="text-white/60 text-xs font-mono">{index + 1}/{images.length}</span>
              )}
            </figcaption>
          </motion.figure>

          {many && (
            <button
              type="button"
              data-testid="lightbox-next-btn"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 md:right-6 z-10 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/25 transition"
              aria-label="Foto berikutnya"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
